import React, { useEffect, useState, useRef } from "react";

import chatbot from "../assets/chatbot-black.webp";
import dreamphoto from "../assets/dreamphoto-bw.webp";
import chatpdf from "../assets/chatpdf-bw.webp";
import flux from "../assets/flux-imagegen.webp";
import teacherai from "../assets/teacherai-bw.webp";
import avatarmakerBG from "../assets/avatarbg.webp";
import chatbotBG from "../assets/chatbot-bg.webp";
import dreamphotoBG from "../assets/dreamphoto-bg.webp";
import chatpdfBG from "../assets/chatpdf-bg.webp";
import fluxBG from "../assets/flux-bg.webp";
import teacheraiBG from "../assets/teacherai-bg.webp";
import avatarLOGO from "../assets/avatar-bw.webp";
import openClawImg from "../assets/open-claw-img.png";
import { AnimatedSection } from "./animation/animateElement";
import "../style.css";
import { ChevronRight } from "lucide-react";
import { GetCookie, SetCookie, RemoveCookie } from "../../core/utils/cookies";

const HomeExtraSections = ({
  tools,
  activeCategory,
}) => {
  const [currentSlide, setCurrentSlide] = useState([0, 0]);
  const [isMobile, setIsMobile] = useState(false);
  const containerRefs = useRef([]);

  const handleOpenClawRedirect = () => {
    const openClawCookie = GetCookie("openclaw");
    const landingpageCookie = GetCookie("landingpage");

    if (!openClawCookie) {
      SetCookie("openclaw", "1", { domain: ".ai-pro.org", path: "/" });
    }
    if (landingpageCookie) {
      RemoveCookie("landingpage");
    }

    window.location.href = "https://ai-pro.org/start-open-claw";
  };

  const selectedApps = [
    "chatbot-pro",
    "dream-photo",
    "chatpdfv2",
    "flux-imagegen",
    "teacher-ai",
    "avatar-maker",
  ];

  const toolsMap = tools.reduce((acc, app) => {
    acc[app.id] = app;
    return acc;
  }, {});

  const appMetadata = [
    {
      id: "chatbot-pro",
      logo: chatbot,
      src: chatbotBG,
      name: "Chatbot Pro",
      desc: "The best of AI, in one simple hub.",
    },
    {
      id: "dream-photo",
      logo: dreamphoto,
      src: dreamphotoBG,
      name: "Dream Photo",
      desc: "Create eye-catching art fast.",
    },
    {
      id: "chatpdfv2",
      logo: chatpdf,
      src: chatpdfBG,
      name: "ChatPDF",
      desc: "Ask your PDFs anything.",
    },
    {
      id: "flux-imagegen",
      logo: flux,
      src: fluxBG,
      name: "Flux ImageGen",
      desc: "Create life-like visuals.",
    },
    {
      id: "teacher-ai",
      logo: teacherai,
      src: teacheraiBG,
      name: "Teacher AI",
      desc: "Learn better, on your terms.",
    },
    {
      id: "avatar-maker",
      logo: avatarLOGO,
      src: avatarmakerBG,
      name: "Avatar Maker",
      desc: "Transform into AI characters.",
    },
  ];

  const appData = selectedApps.map((id) => ({
    ...appMetadata.find((app) => app.id === id),
    link: toolsMap[id]?.link || "#",
  }));

  const appGroups = [];
  for (let i = 0; i < appData.length; i += 3) {
    appGroups.push(appData.slice(i, i + 3));
  }

  useEffect(() => {
    if (activeCategory === "popular" && isMobile) {
      setCurrentSlide([0, 0]);
      containerRefs.current.forEach((container) => {
        if (container) {
          container.scrollTo({
            left: 0,
            behavior: "smooth",
          });
        }
      });
    }
  }, [activeCategory, isMobile]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 500);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    const containers = containerRefs.current.slice();

    const handleScroll = (groupIndex) => () => {
      const container = containers[groupIndex];
      if (!container) return;
      const scrollLeft = container.scrollLeft;
      const pageWidth = container.offsetWidth;
      const newSlide = Math.round(scrollLeft / pageWidth);
      if (newSlide !== currentSlide[groupIndex]) {
        setCurrentSlide((prev) => {
          const newSlides = [...prev];
          newSlides[groupIndex] = newSlide;
          return newSlides;
        });
      }
    };

    containers.forEach((container, index) => {
      if (container) {
        container.addEventListener("scroll", handleScroll(index));
      }
    });

    return () => {
      containers.forEach((container, index) => {
        if (container) {
          container.removeEventListener("scroll", handleScroll(index));
        }
      });
    };
  }, [isMobile, currentSlide]);

  return (
    <div
      className={`max-w-6xl mx-auto py-2 ${
        activeCategory === "popular" ? "px-4" : ""
      }`}
    >
      {/* OpenClaw Section */}
      <AnimatedSection delay={0.6}>
        <div id="open-claw-container" className="flex gap-[70px] flex-col items-center max-w-480:flex-col-reverse lg:flex-row lg:items-center max-w-fit max-w-390:!pb-6 lg:py-[60px] lg:w-screen w-auto relative">
          <div className="mx-0 lg:self-stretch lg:flex lg:flex-col lg:justify-center">
            <p className="text-[36px] text-[#0A0A0A] md:text-[36px] font-semibold font-inter lg:leading-[50px] leading-[40px] 
            lg:text-left text-center">
              Discover the Power of OpenClaw
            </p>
            <div className="text-[16px] lg:text-left text-center lg:mb-0 mb-[10px] text-[#4A5565] leading-[24px] sm:text-[12px] md:text-[16px] tracking-[-0.18px] font-inter mt-[10px]">OpenClaw is a free, open-source AI assistant that lives on your computer — it can see your screen, use your apps, and do real work for you.</div>
            <button onClick={handleOpenClawRedirect}
              className="w-fit items-center lg:min gap-[6px] lg:mx-0 mx-auto flex lg:leading-normal leading-[17px] lg:mb-0 mb-[12px] bg-[#3073D5] hover:bg-[#2590EE] text-white 
              whitespace-nowrap md:whitespace-normal py-[10px] px-[20px] pr-[15px] rounded-full
              transition-colors flex-shrink-0 mt-[16px]"
            >
              Try OpenClaw
              <ChevronRight className="w-[18px] h-[18px]" />
            </button>
          </div>
          <div>
            <img src={openClawImg} alt="OpenClaw" className="lg:max-w-[507px] max-w-auto rounded-[20px] lg:mb-0" />
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default HomeExtraSections;
